import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Loader2, ExternalLink } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function PublicReport() {
  const { token } = useParams();
  const { t, i18n } = useTranslation(["reports"]);
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    axios.get(`${API_BASE}/api/public/reports/${token}`)
      .then(({ data }) => setState({ loading: false, data, error: null }))
      .catch((err) => setState({ loading: false, data: null, error: err?.response?.status || 500 }));
  }, [token]);

  // Public route — flip the html lang attribute to the report language.
  useEffect(() => {
    if (state.data?.language) {
      document.documentElement.lang = state.data.language;
    }
  }, [state.data?.language]);

  if (state.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="max-w-md rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <p className="font-display text-xl font-semibold">{state.error === 404 ? t("public.notFound") : "—"}</p>
        </div>
      </div>
    );
  }

  const r = state.data;
  const evidenceById = Object.fromEntries((r.evidence || []).map((e) => [e.id, e]));

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <article className="mx-auto max-w-3xl rounded-2xl border border-slate-200 bg-white px-10 py-12 shadow-sm" data-testid="public-report">
        <header className="mb-8 border-b border-slate-100 pb-6">
          <p className="text-xs uppercase tracking-wider text-slate-400">{r.brand?.name}</p>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-900">{r.title}</h1>
          <p className="mt-2 text-xs text-slate-400">{t("public.generatedOn", { date: new Date(r.generated_at || Date.now()).toLocaleDateString() })}</p>
        </header>

        {r.sections.map((s) => {
          const c = s.content || {};
          return (
            <section key={s.id} className="mb-10" data-testid={`public-section-${s.type}`}>
              <h2 className="font-display text-xl font-semibold tracking-tight text-slate-900">{s.title}</h2>
              {s.type === "findings" && c.kind === "templated" ? (
                <div className="mt-4 space-y-4">
                  {(c.findings || []).map((f) => (
                    <div key={f.id} className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-display text-sm font-semibold text-slate-900">{f.theme_label || f.type}</p>
                          <p className="mt-1 text-sm leading-relaxed text-slate-700">{f.text}</p>
                          {f.recommended_action && (
                            <p className="mt-3 rounded-lg border-l-2 border-indigo-300 bg-white px-3 py-2 text-xs italic text-slate-600">
                              <span className="font-semibold not-italic">{t("public.recommendedAction")}:</span> {f.recommended_action}
                            </p>
                          )}
                        </div>
                        <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">{Math.round((f.confidence || 0) * 100)}%</span>
                      </div>
                      {(f.supporting_evidence_ids || []).slice(0, 5).map((eid) => {
                        const e = evidenceById[eid];
                        if (!e) return null;
                        return (
                          <blockquote key={eid} className="mt-3 border-l-2 border-slate-200 pl-3 text-xs italic text-slate-500">
                            “{e.quote}”
                            {e.source_url && (
                              <a href={e.source_url} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-0.5 not-italic text-indigo-600 hover:underline">
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </blockquote>
                        );
                      })}
                    </div>
                  ))}
                </div>
              ) : s.type === "methodology" && c.kind === "templated" ? (
                <div className="mt-4">
                  <ul className="grid grid-cols-2 gap-2 text-sm text-slate-600">
                    {Object.entries(c.stats || {}).map(([k, v]) => (
                      <li key={k} className="rounded-lg bg-slate-50 px-3 py-2">
                        <span className="text-slate-400">{k.replace(/_/g, " ")}:</span> <span className="font-medium text-slate-900">{v}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-3 text-xs text-slate-500">{c.confidence_formula}</p>
                </div>
              ) : (
                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{c.text || ""}</p>
              )}
            </section>
          );
        })}

        <footer className="mt-12 border-t border-slate-100 pt-4 text-center text-xs text-slate-400" data-testid="public-report-footer">
          {t("public.generatedBy")}
        </footer>
      </article>
      <div className="mx-auto mt-6 max-w-3xl text-center" data-testid="public-cta">
        <a
          href={`/?utm_source=share&utm_medium=report&utm_campaign=share_cta`}
          className="inline-flex items-center gap-1 rounded-full bg-white px-4 py-2 text-xs font-medium text-slate-500 shadow-sm transition hover:text-indigo-700"
          data-testid="public-cta-link"
        >
          <span className="text-slate-400">{t("public.generatedBy")}</span>
          <span className="mx-1 text-slate-300">·</span>
          <span className="font-semibold text-indigo-600">{t("public.cta")}</span>
        </a>
      </div>
    </div>
  );
}
